import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import { calendarApi, usersApi, candidatesApi, orgApi, supportApi, getErrorMessage } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Settings as SettingsIcon, 
  User, 
  Shield, 
  Bell, 
  Moon, 
  Calendar,
  CheckCircle,
  XCircle,
  ExternalLink,
  Loader2,
  Unlink,
  Lock,
  Eye,
  EyeOff,
  Database,
  AlertTriangle,
  FileX,
  Trash2,
  Building2,
  Sparkles,
  TrendingUp,
  Save,
  LifeBuoy,
  Mail,
  Copy,
  Check,
  Send,
  Clock,
  CheckCircle2,
  MessageSquare,
  Plug,
  PlugZap,
  BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';

import MissingCtcDigestCard from '../components/settings/MissingCtcDigestCard';
import UploadSourceRulesCard from '../components/settings/UploadSourceRulesCard';

const Settings = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, loading: true });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Admin: Resume cleanup state
  const [scanResult, setScanResult] = useState(null); // { total_scanned, valid_resumes, invalid_references, fixed, invalid_candidates }
  const [scanning, setScanning] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [confirmCleanOpen, setConfirmCleanOpen] = useState(false);

  // Organisation (SaaS) state
  const [org, setOrg] = useState(null); // {id, name, slug, plan, slack:{connected,host,channel}, ...}
  const [usage, setUsage] = useState(null); // {plan, plan_label, next_plan, usage:{users,jobs,candidates,clients}}
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgName, setOrgName] = useState('');
  const [savingOrg, setSavingOrg] = useState(false);

  // Slack webhook state
  const [slackUrl, setSlackUrl] = useState('');
  const [savingSlack, setSavingSlack] = useState(false);
  const [testingSlack, setTestingSlack] = useState(false);
  const [showSlackInput, setShowSlackInput] = useState(false);

  // Org-wide default lens for Offer→Joining analytics
  const [defaultJoinBasis, setDefaultJoinBasis] = useState('stage_event');
  const [savingJoinBasis, setSavingJoinBasis] = useState(false);

  // Support contact copy-feedback
  const [supportCopied, setSupportCopied] = useState(false);

  // Support ticket form
  const [ticketMessage, setTicketMessage] = useState('');
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  const fetchTickets = async () => {
    setTicketsLoading(true);
    try {
      const res = await supportApi.listMine();
      setTickets(res.data?.items || []);
    } catch {
      // silent
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  // When deep-linked from the Closure Health Report ("Connect Slack" toast),
  // scroll to the Slack section and open the input automatically.
  useEffect(() => {
    if (searchParams.get('focus') !== 'slack' || orgLoading) return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="org-slack-section"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setShowSlackInput(true);
      }
    }, 100);
    return () => clearTimeout(t);
  }, [searchParams, orgLoading]);

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    const msg = ticketMessage.trim();
    if (msg.length < 5) {
      toast.error('Please describe your issue (5+ characters).');
      return;
    }
    setTicketSubmitting(true);
    try {
      await supportApi.create({
        message: msg,
        page_context: window.location.pathname,
      });
      toast.success("Ticket submitted — we'll get back to you soon.");
      setTicketMessage('');
      fetchTickets();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not submit ticket'));
    } finally {
      setTicketSubmitting(false);
    }
  };

  const isAdminUser = user?.role === 'admin' || user?.role === 'delivery_manager';
  const isOrgAdmin = user?.role === 'admin';

  // Fetch org + usage on mount
  useEffect(() => {
    let alive = true;
    Promise.all([orgApi.getMine().catch(() => null), orgApi.getUsage().catch(() => null)])
      .then(([orgRes, usageRes]) => {
        if (!alive) return;
        const o = orgRes?.data || null;
        setOrg(o);
        setOrgName(o?.name || '');
        if (o?.default_join_basis === 'joining_date' || o?.default_join_basis === 'stage_event') {
          setDefaultJoinBasis(o.default_join_basis);
        }
        setUsage(usageRes?.data || null);
      })
      .finally(() => {
        if (alive) setOrgLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const handleSaveOrgName = async () => {
    const trimmed = orgName.trim();
    if (trimmed.length < 2) {
      toast.error('Organisation name must be at least 2 characters');
      return;
    }
    if (trimmed === org?.name) return;
    setSavingOrg(true);
    try {
      const res = await orgApi.update({ name: trimmed });
      setOrg(res.data);
      toast.success('Organisation renamed');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to rename organisation'));
    } finally {
      setSavingOrg(false);
    }
  };

  // Save or clear the org's Slack Incoming Webhook URL. Expects the full URL
  // from Slack (https://hooks.slack.com/services/T…/B…/…). Blank string clears.
  const handleSaveSlack = async () => {
    const trimmed = slackUrl.trim();
    if (!trimmed) {
      toast.error('Paste a Slack webhook URL, or click Disconnect to clear.');
      return;
    }
    if (!/^https:\/\/hooks\.slack\.com\/services\//.test(trimmed)) {
      toast.error('That does not look like a Slack webhook URL.');
      return;
    }
    setSavingSlack(true);
    try {
      await orgApi.setSlackWebhook(trimmed);
      // Refresh the org so the connected badge updates without a reload
      const fresh = await orgApi.getMine();
      setOrg(fresh.data);
      setSlackUrl('');
      setShowSlackInput(false);
      toast.success('Slack notifications connected');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not save Slack webhook'));
    } finally {
      setSavingSlack(false);
    }
  };

  const handleClearSlack = async () => {
    setSavingSlack(true);
    try {
      await orgApi.setSlackWebhook(null);
      const fresh = await orgApi.getMine();
      setOrg(fresh.data);
      setSlackUrl('');
      setShowSlackInput(false);
      toast.success('Slack notifications disconnected');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not disconnect Slack'));
    } finally {
      setSavingSlack(false);
    }
  };

  const handleTestSlack = async () => {
    setTestingSlack(true);
    try {
      const res = await orgApi.testSlackWebhook();
      if (res.data?.ok) {
        toast.success('Test message sent — check your Slack channel!');
      } else {
        const err = res.data?.error || 'Unknown error';
        toast.error(`Slack rejected the message: ${err}`);
      }
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not send test message'));
    } finally {
      setTestingSlack(false);
    }
  };

  // Persist org-wide default lens for Offer→Joining analytics. Only admins
  // hit this — the input is hidden from everyone else upstream.
  const handleSaveJoinBasis = async (next) => {
    setSavingJoinBasis(true);
    const prev = defaultJoinBasis;
    setDefaultJoinBasis(next); // optimistic
    try {
      await orgApi.setDefaultJoinBasis(next);
      toast.success(
        next === 'joining_date'
          ? 'Org default set to By joining date'
          : 'Org default set to By stage event',
      );
    } catch (e) {
      setDefaultJoinBasis(prev);
      toast.error(getErrorMessage(e, 'Could not save the default lens'));
    } finally {
      setSavingJoinBasis(false);
    }
  };

  const handleScanInvalidResumes = async () => {
    setScanning(true);
    try {
      const res = await candidatesApi.cleanupInvalidResumes(true); // dry_run = true
      setScanResult(res.data);
      const broken = res.data?.invalid_references ?? 0;
      if (broken === 0) {
        toast.success('No broken resume references found.');
      } else {
        toast.warning(`Found ${broken} broken resume reference(s). Review and clean up.`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to scan resume references'));
    } finally {
      setScanning(false);
    }
  };

  const handleCleanInvalidResumes = async () => {
    setCleaning(true);
    try {
      const res = await candidatesApi.cleanupInvalidResumes(false); // dry_run = false
      setScanResult(res.data);
      const fixed = res.data?.fixed ?? 0;
      toast.success(`Cleanup complete. Fixed ${fixed} candidate record(s).`);
      // Hand off the result to the Command Center banner so it can show a
      // green "Cleaned up N candidates" confirmation on next visit. We use
      // sessionStorage so it auto-clears when the tab closes — no global
      // pub/sub needed.
      try {
        sessionStorage.setItem(
          'cc_cleanup_just_ran',
          JSON.stringify({ fixed, at: Date.now() })
        );
        // Clear the 24h dismiss so the banner UI re-evaluates on next mount
        localStorage.removeItem('cc_broken_resumes_dismissed_at');
      } catch {
        /* ignore */
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to clean up resume references'));
    } finally {
      setCleaning(false);
      setConfirmCleanOpen(false);
    }
  };

  useEffect(() => {
    fetchCalendarStatus();
    
    // Check for callback result
    const calendarResult = searchParams.get('calendar');
    if (calendarResult === 'connected') {
      toast.success('Google Calendar connected successfully!');
      fetchCalendarStatus();
    } else if (calendarResult === 'error') {
      const message = searchParams.get('message');
      toast.error(`Failed to connect: ${message || 'Unknown error'}`);
    }
  }, [searchParams]);

  const fetchCalendarStatus = async () => {
    try {
      const response = await calendarApi.getStatus();
      setCalendarStatus({ ...response.data, loading: false });
    } catch (error) {
      setCalendarStatus({ connected: false, loading: false });
    }
  };

  const handleConnectCalendar = async () => {
    setConnecting(true);
    try {
      const response = await calendarApi.getAuthUrl();
      // Redirect to Google OAuth
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to start connection'));
      setConnecting(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Calendar?')) return;
    
    setDisconnecting(true);
    try {
      await calendarApi.disconnect();
      toast.success('Google Calendar disconnected');
      setCalendarStatus({ connected: false, loading: false });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to disconnect'));
    } finally {
      setDisconnecting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return;
    }
    
    setChangingPassword(true);
    try {
      await usersApi.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully!');
      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to change password'));
    } finally {
      setChangingPassword(false);
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-700';
      case 'delivery_manager':
        return 'bg-purple-100 text-purple-700';
      case 'team_lead':
        return 'bg-blue-100 text-blue-700';
      case 'recruiter':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Settings
        </h1>
        <p className="text-slate-600 mt-1">Manage your organisation, account, and preferences</p>
      </div>

      {/* ==================== Organisation card ==================== */}
      {org && (
        <Card data-testid="org-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-teal-700" /> Organisation
            </CardTitle>
            <CardDescription>
              Your workspace identity. Visible to everyone in your organisation.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="org-name" className="text-slate-700 font-medium">
                  Organisation name
                </Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    id="org-name"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    disabled={!isOrgAdmin || savingOrg}
                    minLength={2}
                    maxLength={80}
                    data-testid="org-name-input"
                  />
                  {isOrgAdmin && (
                    <Button
                      onClick={handleSaveOrgName}
                      disabled={
                        savingOrg ||
                        orgName.trim() === org.name ||
                        orgName.trim().length < 2
                      }
                      className="bg-teal-700 hover:bg-teal-800"
                      data-testid="org-name-save-btn"
                    >
                      {savingOrg ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
                {!isOrgAdmin && (
                  <p className="text-xs text-slate-500 mt-1">
                    Only org admins can change this.
                  </p>
                )}
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Workspace ID</Label>
                <div className="flex items-center gap-2 mt-1.5 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-xs font-mono text-slate-600 truncate">
                  {org.slug || org.id}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Stable identifier — used for support and exports.
                </p>
              </div>
            </div>

            {/* ---- Slack notifications sub-section ---- */}
            <div className="pt-4 border-t border-slate-100" data-testid="org-slack-section">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-violet-600 flex-shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Slack notifications</div>
                    <div className="text-xs text-slate-500">
                      Ping your team channel whenever the monthly closure goal changes.
                    </div>
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  {org.slack?.connected ? (
                    <Badge
                      className="bg-emerald-100 text-emerald-800 border-emerald-200"
                      data-testid="org-slack-status-connected"
                    >
                      <PlugZap className="w-3 h-3 mr-1" /> Connected to {org.slack.host || 'Slack'}
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="bg-slate-50 text-slate-600 border-slate-200"
                      data-testid="org-slack-status-disconnected"
                    >
                      <Plug className="w-3 h-3 mr-1" /> Not connected
                    </Badge>
                  )}
                </div>
              </div>

              {isOrgAdmin ? (
                <div className="mt-3 space-y-2">
                  {!showSlackInput && !org.slack?.connected ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSlackInput(true)}
                      data-testid="org-slack-add-btn"
                    >
                      <Plug className="w-3.5 h-3.5 mr-1.5" /> Connect Slack
                    </Button>
                  ) : null}

                  {org.slack?.connected && !showSlackInput ? (
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleTestSlack}
                        disabled={testingSlack}
                        data-testid="org-slack-test-btn"
                      >
                        {testingSlack ? (
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5 mr-1.5" />
                        )}
                        Send test message
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSlackInput(true)}
                        data-testid="org-slack-edit-btn"
                      >
                        Replace webhook
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleClearSlack}
                        disabled={savingSlack}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        data-testid="org-slack-disconnect-btn"
                      >
                        <Unlink className="w-3.5 h-3.5 mr-1.5" />
                        Disconnect
                      </Button>
                    </div>
                  ) : null}

                  {showSlackInput ? (
                    <div className="space-y-2">
                      <Label htmlFor="slack-webhook-input" className="text-xs text-slate-600">
                        Paste your Slack Incoming Webhook URL
                      </Label>
                      <div className="flex gap-2 flex-wrap">
                        <Input
                          id="slack-webhook-input"
                          type="url"
                          value={slackUrl}
                          onChange={(e) => setSlackUrl(e.target.value)}
                          placeholder="https://hooks.slack.com/services/T…/B…/…"
                          disabled={savingSlack}
                          className="font-mono text-xs flex-1 min-w-[280px]"
                          data-testid="org-slack-url-input"
                          autoFocus
                        />
                        <Button
                          size="sm"
                          onClick={handleSaveSlack}
                          disabled={savingSlack || !slackUrl.trim()}
                          className="bg-teal-700 hover:bg-teal-800"
                          data-testid="org-slack-save-btn"
                        >
                          {savingSlack ? (
                            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          ) : (
                            <Save className="w-3.5 h-3.5 mr-1.5" />
                          )}
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowSlackInput(false);
                            setSlackUrl('');
                          }}
                          disabled={savingSlack}
                          data-testid="org-slack-cancel-btn"
                        >
                          Cancel
                        </Button>
                      </div>
                      <p className="text-[11px] text-slate-500">
                        Create one in Slack → <em>Apps → Incoming Webhooks → Add to Slack</em>, pick a
                        channel (e.g. <span className="font-mono">#hiring</span>), and paste the URL here.
                        We never display this URL once saved.
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="text-xs text-slate-500 mt-2">
                  Only org admins can manage the Slack connection.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Missing-CTC Monday digest — admin-only, depends on the Slack webhook above */}
      {isOrgAdmin && <MissingCtcDigestCard />}

      {/* Upload Source rules — admin-only. Wire new portals (Hirist, Cutshort, …) at runtime. */}
      {isOrgAdmin && <UploadSourceRulesCard />}

      {/* ==================== Analytics defaults (admin-only) ==================== */}
      {isOrgAdmin && (
        <Card data-testid="analytics-defaults-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-teal-600" />
              Analytics defaults
            </CardTitle>
            <CardDescription>
              Pick the starting lens for the Offer → Joining card. Individual
              users can still flip their session via the pill toggle on the card.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Offer → Joining bucket lens
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleSaveJoinBasis('stage_event')}
                disabled={savingJoinBasis}
                className={`text-left rounded-lg border p-3 transition ${
                  defaultJoinBasis === 'stage_event'
                    ? 'border-teal-500 bg-teal-50/50 ring-2 ring-teal-200'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid="org-default-join-basis-stage-event"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-900">By stage event</span>
                  {defaultJoinBasis === 'stage_event' && (
                    <span className="text-[10px] font-bold text-teal-700 bg-teal-100 rounded px-1.5 py-0.5">
                      ACTIVE
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 leading-snug">
                  Bucket joins by when the JOINED stage was set. Measures funnel
                  velocity — what your recruiters closed this period. <br/>
                  <span className="text-slate-400">Recommended for: Ops, recruiter performance reviews.</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => handleSaveJoinBasis('joining_date')}
                disabled={savingJoinBasis}
                className={`text-left rounded-lg border p-3 transition ${
                  defaultJoinBasis === 'joining_date'
                    ? 'border-teal-500 bg-teal-50/50 ring-2 ring-teal-200'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid="org-default-join-basis-joining-date"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-900">By joining date</span>
                  {defaultJoinBasis === 'joining_date' && (
                    <span className="text-[10px] font-bold text-teal-700 bg-teal-100 rounded px-1.5 py-0.5">
                      ACTIVE
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 leading-snug">
                  Bucket joins by the candidate's contractual joining_date.
                  Measures actual headcount &amp; revenue recognized this period. <br/>
                  <span className="text-slate-400">Recommended for: Finance, leadership KPIs.</span>
                </div>
              </button>
            </div>
            <p className="text-[11px] text-slate-500">
              This sets the default for new visitors. Anyone who flips the toggle
              on the card will keep their own preference (stored in their browser).
            </p>
          </CardContent>
        </Card>
      )}

      {/* ==================== Plan & Usage card ==================== */}
      {usage && (
        <Card data-testid="plan-card">
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-600" /> Plan &amp; Usage
              </span>
              <Badge
                className={
                  usage.plan === 'enterprise'
                    ? 'bg-violet-100 text-violet-800 border-violet-200'
                    : usage.plan === 'pro'
                    ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                    : usage.plan === 'starter'
                    ? 'bg-sky-100 text-sky-800 border-sky-200'
                    : 'bg-slate-100 text-slate-700 border-slate-200'
                }
                data-testid="plan-badge"
              >
                {usage.plan_label} plan
              </Badge>
            </CardTitle>
            <CardDescription>
              {usage.plan === 'enterprise'
                ? 'Your plan has unlimited resources.'
                : `You're on the ${usage.plan_label} plan. ${
                    usage.next_plan_label
                      ? `Upgrade to ${usage.next_plan_label} for higher limits.`
                      : ''
                  }`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { key: 'users', label: 'Users' },
                { key: 'jobs', label: 'Jobs' },
                { key: 'candidates', label: 'Candidates' },
                { key: 'clients', label: 'Clients' },
              ].map((r) => {
                const u = usage.usage?.[r.key] || { used: 0, limit: null, at_limit: false };
                return (
                  <div
                    key={r.key}
                    className="rounded-lg border border-slate-200 bg-slate-50/50 p-3"
                    data-testid={`plan-card-${r.key}`}
                  >
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">
                        {r.label}
                      </span>
                      <span
                        className={`text-xs font-mono font-semibold ${
                          u.at_limit ? 'text-rose-700' : 'text-slate-700'
                        }`}
                      >
                        {u.used}
                        {u.limit != null ? ` / ${u.limit}` : ' / ∞'}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                      <div
                        className={`h-full transition-[width] ${
                          u.at_limit
                            ? 'bg-rose-500'
                            : u.pct >= 80
                            ? 'bg-amber-500'
                            : 'bg-teal-500'
                        }`}
                        style={{
                          width: u.limit
                            ? `${Math.min(100, (u.used / u.limit) * 100)}%`
                            : '0%',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {usage.plan !== 'enterprise' && (
              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-slate-500">
                  Need more headroom? Compare plans and upgrade in seconds.
                </p>
                <Link to="/pricing" data-testid="plan-card-upgrade">
                  <Button
                    size="sm"
                    className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white hover:opacity-90"
                  >
                    <TrendingUp className="w-4 h-4 mr-1" /> See upgrade options
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {orgLoading && (
        <div
          className="text-center py-6 text-slate-400 text-sm"
          data-testid="org-loading"
        >
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
          Loading organisation…
        </div>
      )}

      {/* ==================== Team card (admin shortcut) ==================== */}
      {isOrgAdmin && usage && (
        <Card data-testid="team-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-sky-700" /> Team
            </CardTitle>
            <CardDescription>
              Invite teammates and manage roles. Each new user counts toward your plan limit.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/50 px-4 py-3">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-sky-700" />
                </div>
                <div>
                  <p
                    className="text-sm font-semibold text-slate-900"
                    data-testid="team-card-count"
                  >
                    {usage.usage?.users?.used ?? 0}
                    {usage.usage?.users?.limit
                      ? ` / ${usage.usage.users.limit}`
                      : ''}{' '}
                    {(usage.usage?.users?.used ?? 0) === 1 ? 'member' : 'members'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {usage.usage?.users?.at_limit
                      ? 'You\'ve hit your plan limit. Upgrade to invite more.'
                      : 'Click manage team to invite or change roles.'}
                  </p>
                </div>
              </div>
              <Link to="/users" data-testid="team-card-cta">
                <Button variant="outline" size="sm">
                  Manage team <ExternalLink className="w-3.5 h-3.5 ml-1" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ==================== Support contact card ==================== */}
      <Card data-testid="support-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-amber-600" /> Support
          </CardTitle>
          <CardDescription>
            Need help or want to report an issue? Submit a ticket below — our team usually replies
            within one business day.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Ticket form */}
          <form
            onSubmit={handleSubmitTicket}
            className="space-y-2"
            data-testid="support-ticket-form"
          >
            <Label
              htmlFor="ticket-msg"
              className="text-xs uppercase tracking-wide text-slate-500 font-medium"
            >
              Describe your issue
            </Label>
            <Textarea
              id="ticket-msg"
              value={ticketMessage}
              onChange={(e) => setTicketMessage(e.target.value)}
              placeholder="What broke, what's confusing, or what would you like to see?"
              rows={3}
              maxLength={2000}
              disabled={ticketSubmitting}
              data-testid="support-ticket-input"
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-slate-400">
                We attach this page's URL ({window.location.pathname}) to help us debug.
              </span>
              <Button
                type="submit"
                size="sm"
                disabled={ticketSubmitting || ticketMessage.trim().length < 5}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="support-ticket-submit"
              >
                {ticketSubmitting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                ) : (
                  <Send className="w-3.5 h-3.5 mr-1" />
                )}
                Submit ticket
              </Button>
            </div>
          </form>

          {/* Email fallback row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2.5">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">
                Or email us directly
              </p>
              <p
                className="text-xs font-mono font-semibold text-slate-900 truncate"
                data-testid="support-email"
              >
                support@interviewwala.com
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-7"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText('support@interviewwala.com');
                  setSupportCopied(true);
                  toast.success('Support email copied');
                  setTimeout(() => setSupportCopied(false), 2000);
                } catch {
                  toast.error('Could not copy — please copy manually.');
                }
              }}
              data-testid="support-copy-btn"
            >
              {supportCopied ? (
                <Check className="w-3 h-3 mr-1 text-emerald-600" />
              ) : (
                <Copy className="w-3 h-3 mr-1" />
              )}
              {supportCopied ? 'Copied' : 'Copy'}
            </Button>
          </div>

          {/* Recent tickets */}
          <div data-testid="support-ticket-list">
            <p className="text-xs uppercase tracking-wide text-slate-500 font-medium mb-2">
              Your recent tickets
            </p>
            {ticketsLoading ? (
              <p className="text-xs text-slate-400">
                <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Loading…
              </p>
            ) : tickets.length === 0 ? (
              <p className="text-xs text-slate-400 italic">
                No tickets yet. Use the form above to send your first one.
              </p>
            ) : (
              <ul className="space-y-2 max-h-[260px] overflow-auto">
                {tickets.slice(0, 10).map((t) => (
                  <li
                    key={t.id}
                    className="rounded-md border border-slate-200 bg-white px-3 py-2"
                    data-testid={`support-ticket-row-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm text-slate-800 break-words flex-1 line-clamp-2">
                        {t.message}
                      </p>
                      <Badge
                        className={
                          t.status === 'resolved'
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200 flex-shrink-0'
                            : 'bg-amber-100 text-amber-800 border-amber-200 flex-shrink-0'
                        }
                      >
                        {t.status === 'resolved' ? (
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                        ) : (
                          <Clock className="w-3 h-3 mr-1" />
                        )}
                        {t.status}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[11px] text-slate-500">
                      <span>{t.user_name || 'Someone'}</span>
                      {t.page_context && (
                        <>
                          <span>·</span>
                          <span className="font-mono">{t.page_context}</span>
                        </>
                      )}
                      <span>·</span>
                      <span>
                        {new Date(t.created_at).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" /> Profile
          </CardTitle>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <Avatar className="h-20 w-20">
              <AvatarFallback className="bg-teal-100 text-teal-700 text-2xl font-semibold">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-slate-900">{user?.name}</h3>
              <p className="text-slate-500">{user?.email}</p>
              <Badge className={getRoleBadgeColor(user?.role)}>
                {user?.role?.replace('_', ' ')}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admin: Data Maintenance (Resume Cleanup) */}
      {isAdminUser && (
        <Card data-testid="admin-data-maintenance-card" className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-amber-600" /> Data Maintenance
            </CardTitle>
            <CardDescription>
              Admin-only tools to keep production data clean and consistent.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 space-y-1">
                  <p className="font-medium text-amber-900">Clean Up Broken Resume Links</p>
                  <p className="text-sm text-amber-800">
                    Some legacy candidates have a stored resume reference but the underlying file is
                    missing on the server. This tool finds those records and clears the broken
                    references so the UI shows the "Re-Upload Resume" option instead of a 404.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={handleScanInvalidResumes}
                disabled={scanning || cleaning}
                data-testid="scan-invalid-resumes-btn"
              >
                {scanning ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <FileX className="w-4 h-4 mr-2" />
                )}
                Scan for Broken Resume Links
              </Button>

              <Button
                variant="destructive"
                onClick={() => setConfirmCleanOpen(true)}
                disabled={
                  cleaning ||
                  scanning ||
                  !scanResult ||
                  (scanResult?.invalid_references ?? 0) === 0
                }
                data-testid="clean-invalid-resumes-btn"
              >
                {cleaning ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Clean Up Now
                {scanResult?.dry_run && (scanResult?.invalid_references ?? 0) > 0
                  ? ` (${scanResult.invalid_references})`
                  : ''}
              </Button>
            </div>

            {scanResult && (
              <div
                className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3"
                data-testid="resume-cleanup-results"
              >
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  <div className="p-2 bg-white rounded border border-slate-200">
                    <p className="text-xs text-slate-500">Scanned</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {scanResult.total_scanned ?? 0}
                    </p>
                  </div>
                  <div className="p-2 bg-white rounded border border-slate-200">
                    <p className="text-xs text-slate-500">Valid</p>
                    <p className="text-lg font-semibold text-green-700">
                      {scanResult.valid_resumes ?? 0}
                    </p>
                  </div>
                  <div className="p-2 bg-white rounded border border-slate-200">
                    <p className="text-xs text-slate-500">Broken</p>
                    <p className="text-lg font-semibold text-red-700">
                      {scanResult.invalid_references ?? 0}
                    </p>
                  </div>
                  <div className="p-2 bg-white rounded border border-slate-200">
                    <p className="text-xs text-slate-500">Fixed</p>
                    <p className="text-lg font-semibold text-teal-700">
                      {scanResult.fixed ?? 0}
                    </p>
                  </div>
                </div>

                {scanResult.dry_run && (scanResult.invalid_references ?? 0) > 0 && (
                  <p className="text-xs text-amber-700">
                    Dry run only — no changes made. Click "Clean Up Now" to clear broken
                    references.
                  </p>
                )}

                {scanResult.note && (
                  <p className="text-xs text-slate-500">{scanResult.note}</p>
                )}

                {Array.isArray(scanResult.invalid_candidates) &&
                  scanResult.invalid_candidates.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">
                        Affected candidates ({scanResult.invalid_candidates.length} shown):
                      </p>
                      <ScrollArea className="h-48 rounded border border-slate-200 bg-white">
                        <ul className="divide-y divide-slate-100">
                          {scanResult.invalid_candidates.map((c, idx) => (
                            <li
                              key={c.id || idx}
                              className="px-3 py-2 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1"
                              data-testid={`broken-resume-row-${idx}`}
                            >
                              <span className="font-medium text-slate-800">
                                {c.name || 'Unknown'}
                              </span>
                              <span className="text-slate-500 text-xs truncate">
                                {c.email || '—'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </ScrollArea>
                    </div>
                  )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Change Password Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" /> Change Password
          </CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            {/* Current Password */}
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Confirm New Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={changingPassword}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="change-password-btn"
            >
              {changingPassword ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Changing...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Change Password
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Google Calendar Integration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" /> Google Calendar
          </CardTitle>
          <CardDescription>Connect your Google Calendar to sync interviews</CardDescription>
        </CardHeader>
        <CardContent>
          {calendarStatus.loading ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Checking connection...
            </div>
          ) : calendarStatus.connected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div className="flex-1">
                  <p className="font-medium text-green-800">Connected</p>
                  <p className="text-sm text-green-600">
                    Your Google Calendar is connected. Interview events will sync automatically.
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleDisconnectCalendar}
                  disabled={disconnecting}
                  data-testid="disconnect-calendar-btn"
                >
                  {disconnecting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Unlink className="w-4 h-4 mr-2" />
                  )}
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                <XCircle className="w-6 h-6 text-slate-400" />
                <div className="flex-1">
                  <p className="font-medium text-slate-700">Not Connected</p>
                  <p className="text-sm text-slate-500">
                    Connect your Google Calendar to automatically create events for scheduled interviews.
                  </p>
                </div>
              </div>
              <Button
                onClick={handleConnectCalendar}
                disabled={connecting}
                className="bg-teal-700 hover:bg-teal-800"
                data-testid="connect-calendar-btn"
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Calendar className="w-4 h-4 mr-2" />
                )}
                Connect Google Calendar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Permissions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" /> Role Permissions
          </CardTitle>
          <CardDescription>What you can do with your current role</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {user?.role === 'admin' && (
              <>
                <PermissionItem text="Full access to all features" />
                <PermissionItem text="Manage users and roles" />
                <PermissionItem text="Delete jobs, candidates, and applications" />
                <PermissionItem text="View audit logs and reports" />
              </>
            )}
            {user?.role === 'delivery_manager' && (
              <>
                <PermissionItem text="Manage clients and jobs" />
                <PermissionItem text="View all recruiters' work" />
                <PermissionItem text="Access reports and analytics" />
                <PermissionItem text="Approve job requisitions" />
              </>
            )}
            {user?.role === 'team_lead' && (
              <>
                <PermissionItem text="Create and manage jobs" />
                <PermissionItem text="View team performance" />
                <PermissionItem text="Manage candidates and applications" />
              </>
            )}
            {user?.role === 'recruiter' && (
              <>
                <PermissionItem text="View assigned jobs" />
                <PermissionItem text="Add and manage candidates" />
                <PermissionItem text="Move candidates through pipeline" />
                <PermissionItem text="Schedule interviews" />
              </>
            )}
            {user?.role === 'client_spoc' && (
              <>
                <PermissionItem text="View candidates for your company" />
                <PermissionItem text="Provide interview feedback" />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Coming Soon Features */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5" /> Coming Soon
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg opacity-60">
              <Bell className="w-5 h-5 text-slate-400 mb-2" />
              <p className="font-medium text-slate-700">Notification Preferences</p>
              <p className="text-sm text-slate-500">Customize your alerts</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg opacity-60">
              <Moon className="w-5 h-5 text-slate-400 mb-2" />
              <p className="font-medium text-slate-700">Dark Mode</p>
              <p className="text-sm text-slate-500">Toggle dark theme</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirm cleanup dialog */}
      <AlertDialog open={confirmCleanOpen} onOpenChange={setConfirmCleanOpen}>
        <AlertDialogContent data-testid="confirm-cleanup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clean up broken resume links?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently clear the resume reference from{' '}
              <span className="font-semibold text-red-700">
                {scanResult?.invalid_references ?? 0}
              </span>{' '}
              candidate record(s) whose underlying file is missing. Candidates will then show the
              "Re-Upload Resume" option. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cleaning} data-testid="confirm-cleanup-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCleanInvalidResumes}
              disabled={cleaning}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cleanup-confirm"
            >
              {cleaning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Cleaning...
                </>
              ) : (
                'Yes, clean up'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const PermissionItem = ({ text }) => (
  <div className="flex items-center gap-2">
    <div className="w-2 h-2 bg-teal-500 rounded-full" />
    <span className="text-slate-600">{text}</span>
  </div>
);

export default Settings;
